import { InheritableEventEmitter } from "../inheritableeventemitter";

export type TabOptions = {
    /** デフォルトで選択されているタブかどうかを判断する関数 */
    defaultElementSelector?: (element: HTMLLIElement) => boolean;
} & ({
    /** タブ要素を検索するセレクター文字列 */
    selector: string;
} | {
    /** タブ要素 */
    rootElement: HTMLElement;
});

/**
 * Bulmaのタブクラスで作成された要素を管理するクラス。
 */
export class Tabs
    extends InheritableEventEmitter<{
        change: [element: HTMLLIElement, index: number];
    }>{

    #tabItems: HTMLLIElement[];
    #activeIndex = 0;

    /**
     * コンストラクター。
     *
     * @param options オプション
     */
    constructor(options: TabOptions) {
        super();

        let element;
        if ("rootElement" in options && options.rootElement) {
            element = options.rootElement;
        } else if("selector" in options && options.selector) {
            element = document.querySelector<HTMLElement>(options.selector);
        }

        if (!element) {
            throw new Error("element is not found.");
        }

        this.#tabItems = [...element.getElementsByTagName("li")];

        if (this.#tabItems.length === 0) {
            throw new Error("no tab items.");
        }

        if (this.#tabItems.filter((x) => x.classList.contains("is-active")).length > 1) {
            throw new Error("the activated tab should be one.");
        }

        this.#initializeTabs(options.defaultElementSelector);
    }

    /**
     * タブの初期化処理を行う。
     *
     * @param defaultElementSelector デフォルトで選択状態とするタブかどうかを判定する関数
     */
    #initializeTabs(defaultElementSelector?: ((element: HTMLLIElement) => boolean)) {
        // 各タブにクリックイベントを設定
        const eventName = (document.body.ontouchend) ? "touchend" : "click";
        this.#tabItems.forEach((tab, index) => {
            tab.addEventListener(eventName, () => this.activateTab(index));
        });

        // 初期状態を設定（すでにis-activeがある場合はそれを優先）
        if (defaultElementSelector) {
            let isSet = false;
            for (const item of this.#tabItems) {
                if (isSet) {
                    item.classList.remove("is-active");
                    continue;
                }

                if (defaultElementSelector(item)) {
                    item.classList.add("is-active");
                    isSet = true;
                } else {
                    item.classList.remove("is-active");
                }
            }
        }

        for (let i = 0; i < this.#tabItems.length; i++) {
            if (this.#tabItems[i].classList.contains("is-active")) {
                this.#activeIndex = i;
                break;
            }
        }
    }

    /**
     * 指定したインデックスのタブをアクティブにする。
     *
     * @param index アクティブにするタブのインデックス
     */
    activateTab(index: number) {
        if (index < 0 || index >= this.#tabItems.length) {
            throw new Error(`index is out of bound. index: ${index}`);
        }

        // 以前のアクティブタブからクラスを削除
        this.#tabItems[this.#activeIndex].classList.remove("is-active");

        // 新しいタブをアクティブに
        this.#tabItems[index].classList.add("is-active");

        // アクティブインデックスを更新
        this.#activeIndex = index;

        // タブ切り替えイベントを発火
        this.emit("change", this.#tabItems[index], index);
    }

    /**
     * 現在アクティブなタブのインデックスを返す。
     *
     * @returns タブのインデックス
     */
    getActiveTabIndex() {
        return this.#activeIndex;
    }

    getActiveTabElement() {
        return this.#tabItems[this.#activeIndex];
    }
}
